import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, Star, Package } from 'lucide-react';
import { 
  VendorService, 
  useVendorServices, 
  useCreateVendorService, 
  useUpdateVendorService, 
  useDeleteVendorService 
} from '@/hooks/useVendor';

interface VendorServicesTabProps {
  vendorId: string;
}

const PRICE_TYPES = [
  { value: 'fixed', label: 'Precio fijo' },
  { value: 'starting_from', label: 'Desde' },
  { value: 'per_hour', label: 'Por hora' },
  { value: 'per_guest', label: 'Por invitado' },
  { value: 'custom', label: 'A consultar' },
];

const VendorServicesTab = ({ vendorId }: VendorServicesTabProps) => {
  const { data: services = [], isLoading } = useVendorServices(vendorId);
  const createService = useCreateVendorService();
  const updateService = useUpdateVendorService();
  const deleteService = useDeleteVendorService();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<VendorService | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    price_type: 'fixed',
    is_featured: false,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      price_type: 'fixed',
      is_featured: false,
    });
    setEditingService(null);
  };

  const openEditDialog = (service: VendorService) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      price: service.price?.toString() || '',
      price_type: service.price_type,
      is_featured: service.is_featured,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const serviceData = {
      vendor_id: vendorId,
      name: formData.name,
      description: formData.description || null,
      price: formData.price ? parseFloat(formData.price) : null,
      price_type: formData.price_type,
      is_featured: formData.is_featured,
      display_order: services.length,
      image: null,
    };

    if (editingService) {
      updateService.mutate(
        { id: editingService.id, ...serviceData },
        {
          onSuccess: () => {
            setIsDialogOpen(false);
            resetForm();
          },
        }
      );
    } else {
      createService.mutate(serviceData, {
        onSuccess: () => {
          setIsDialogOpen(false);
          resetForm();
        },
      });
    }
  };

  const handleDelete = (service: VendorService) => {
    if (confirm('¿Estás seguro de eliminar este servicio?')) {
      deleteService.mutate({ id: service.id, vendorId });
    }
  };

  const formatPrice = (service: VendorService) => {
    if (!service.price && service.price_type !== 'custom') return 'Sin precio';
    if (service.price_type === 'custom') return 'A consultar';
    
    const formatted = new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(service.price || 0);

    switch (service.price_type) {
      case 'starting_from':
        return `Desde ${formatted}`;
      case 'per_hour':
        return `${formatted}/hora`;
      case 'per_guest':
        return `${formatted}/invitado`;
      default:
        return formatted;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Mis Servicios</h2>
          <p className="text-muted-foreground">
            Añade los servicios o paquetes que ofreces
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Añadir Servicio
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
              </DialogTitle>
              <DialogDescription>
                {editingService ? 'Modifica los datos del servicio' : 'Añade un nuevo servicio o paquete'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="service-name">Nombre del Servicio</Label>
                <Input
                  id="service-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Sesión completa de boda"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-description">Descripción</Label>
                <Textarea
                  id="service-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe qué incluye este servicio..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service-price">Precio</Label>
                  <Input
                    id="service-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price-type">Tipo de Precio</Label>
                  <Select
                    value={formData.price_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, price_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  className="rounded border-border"
                />
                <Label htmlFor="is-featured" className="text-sm">
                  Destacar este servicio
                </Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={createService.isPending || updateService.isPending}>
                  {editingService ? 'Guardar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {services.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Sin servicios</h3>
            <p className="text-muted-foreground text-center mb-4">
              Añade tus servicios o paquetes para que las parejas puedan ver lo que ofreces
            </p>
            <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Añadir Primer Servicio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map(service => (
            <Card key={service.id} className="relative group">
              {service.is_featured && (
                <Badge className="absolute top-3 right-3 gap-1" variant="default">
                  <Star className="h-3 w-3 fill-current" />
                  Destacado
                </Badge>
              )}
              <CardHeader>
                <CardTitle className="text-lg">{service.name}</CardTitle>
                <CardDescription className="text-primary font-semibold">
                  {formatPrice(service)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {service.description || 'Sin descripción'}
                </p>
                <div className="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(service)}
                    className="gap-1"
                  >
                    <Edit2 className="h-3 w-3" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(service)}
                    className="gap-1 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorServicesTab;
